import { API_BASE_URL as API_BASE, NEWS_API } from '../config/api';
import { createAuthenticatedApiClient } from './authenticatedApiClient';
import {
  createApiResponse,
  createPaginatedResponse,
  createNewsArticle,
  createNewsStatistics,
  createPagination
} from '../models';
import { clearRequestCacheByPrefix, withRequestCache } from './requestCache';
const apiClient = createAuthenticatedApiClient({
  baseURL: `${API_BASE}/api/v1/news`,
});

export const newsService = {
  /**
   * Risk Intelligence Feed: paginated articles with filters.
   * GET /api/v1/news/articles — q, topic, region, severity, ticker, industry, dateFrom, dateTo, page, size.
   */
  async getNewsArticles(params = {}) {
    try {
      const {
        q,
        topic,
        region,
        severity,
        ticker,
        industry,
        dateFrom,
        dateTo,
        page = 1,
        size = 20,
      } = params;
      const searchParams = new URLSearchParams();
      if (q != null && String(q).trim()) searchParams.set('q', String(q).trim());
      if (topic && topic !== 'all') searchParams.set('topic', topic);
      if (region && region !== 'all') searchParams.set('region', region);
      if (severity && severity !== 'all') searchParams.set('severity', severity);
      const tickerVal = ticker != null && String(ticker).trim() ? String(ticker).trim() : null;
      if (tickerVal) searchParams.set('ticker', tickerVal);
      if (industry) searchParams.set('industry', industry);
      if (dateFrom) searchParams.set('dateFrom', dateFrom);
      if (dateTo) searchParams.set('dateTo', dateTo);
      searchParams.set('page', String(page));
      searchParams.set('size', String(Math.min(100, Math.max(1, size))));

      const response = await apiClient.get(`${NEWS_API.ARTICLES}?${searchParams.toString()}`);
      const { content = [], pagination: paginationData, success, message } = response.data;

      if (!success) {
        return createApiResponse({ success: false, message: message || 'Failed to fetch news articles' });
      }

      const articles = Array.isArray(content) ? content.map((a) => createNewsArticle(a)) : [];
      const pagination = createPagination(paginationData || {});

      return {
        success: true,
        data: articles,
        pagination,
        message,
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch news articles',
      });
    }
  },

  /**
   * Filter options for topic, region, severity dropdowns.
   * GET /api/v1/news/filters
   */
  async getFilters() {
    try {
      const response = await withRequestCache({
        key: 'news:filters',
        ttlMs: 10 * 60 * 1000,
        requestFn: () => apiClient.get(NEWS_API.FILTERS),
      });
      const { success, data, message } = response.data;
      if (!success) {
        return createApiResponse({ success: false, message: message || 'Failed to fetch filters' });
      }
      return {
        success: true,
        data: {
          topics: data?.topics ?? [],
          regions: data?.regions ?? [],
          severityLevels: data?.severityLevels ?? [],
        },
        message,
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch filters',
      });
    }
  },

  /**
   * Quick filter counts for sidebar: allNews, criticalAlerts, highPriority, savedArticles, watchlistNews.
   * GET /api/v1/news/quick-filter-counts — userId required for saved/watchlist; optional ticker, industry, dateFrom, dateTo.
   */
  async getQuickFilterCounts(userId, filters = {}) {
    try {
      const sp = new URLSearchParams();
      if (userId) sp.set('userId', userId);
      const { ticker, industry, dateFrom, dateTo } = filters;
      if (ticker) sp.set('ticker', ticker);
      if (industry) sp.set('industry', industry);
      if (dateFrom) sp.set('dateFrom', dateFrom);
      if (dateTo) sp.set('dateTo', dateTo);
      const response = await apiClient.get(`${NEWS_API.QUICK_FILTER_COUNTS}?${sp.toString()}`);
      const { success, data, message } = response.data;
      if (!success) {
        return createApiResponse({ success: false, message: message || 'Failed to fetch quick filter counts' });
      }
      return {
        success: true,
        data: {
          allNews: data?.allNews ?? 0,
          criticalAlerts: data?.criticalAlerts ?? 0,
          highPriority: data?.highPriority ?? 0,
          savedArticles: data?.savedArticles ?? 0,
          watchlistNews: data?.watchlistNews ?? 0,
        },
        message,
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch quick filter counts',
      });
    }
  },

  /**
   * Trending topics for sidebar.
   * GET /api/v1/news/trending-topics — limit, window.
   */
  async getTrendingTopics(limit = 10, windowDays = 7) {
    try {
      const response = await withRequestCache({
        key: `news:trending:${limit}:${windowDays}`,
        ttlMs: 5 * 60 * 1000,
        requestFn: () => apiClient.get(NEWS_API.TRENDING_TOPICS, {
          params: { limit, window: windowDays },
        }),
      });
      const { success, data, message } = response.data;
      if (!success) {
        return createApiResponse({ success: false, message: message || 'Failed to fetch trending topics' });
      }
      return {
        success: true,
        data: Array.isArray(data) ? data : [],
        message,
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch trending topics',
      });
    }
  },

  /**
   * Save article for user. POST /api/v1/news/articles/:articleId/save?userId=...
   */
  async saveArticle(articleId, userId) {
    try {
      if (!userId) {
        return createApiResponse({ success: false, message: 'User ID required to save article' });
      }
      const response = await apiClient.post(
        `${NEWS_API.SAVE_ARTICLE(articleId)}?userId=${encodeURIComponent(userId)}`
      );
      clearRequestCacheByPrefix(`news:saved:${userId}`);
      const { success, message } = response.data;
      return createApiResponse({ success: success ?? true, message: message || 'Article saved' });
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to save article',
      });
    }
  },

  /**
   * Unsave article. DELETE /api/v1/news/articles/:articleId/save?userId=...
   */
  async unsaveArticle(articleId, userId) {
    try {
      if (!userId) {
        return createApiResponse({ success: false, message: 'User ID required to unsave article' });
      }
      const response = await apiClient.delete(
        `${NEWS_API.SAVE_ARTICLE(articleId)}?userId=${encodeURIComponent(userId)}`
      );
      clearRequestCacheByPrefix(`news:saved:${userId}`);
      const { success, message } = response.data;
      return createApiResponse({ success: success ?? true, message: message || 'Article removed from saved' });
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to unsave article',
      });
    }
  },

  /**
   * Saved articles list for current user. GET /api/v1/news/saved-articles?userId=...
   */
  async getSavedArticles(userId) {
    try {
      if (!userId) {
        return createApiResponse({ success: false, message: 'User ID required', data: [] });
      }
      const response = await withRequestCache({
        key: `news:saved:${userId}`,
        ttlMs: 60 * 1000,
        requestFn: () => apiClient.get(
          `${NEWS_API.SAVED_ARTICLES}?userId=${encodeURIComponent(userId)}`
        ),
      });
      const { success, data, message } = response.data;
      if (!success) {
        return createApiResponse({ success: false, message: message || 'Failed to fetch saved articles', data: [] });
      }
      const list = Array.isArray(data) ? data.map((a) => createNewsArticle(a)) : [];
      return { success: true, data: list, message };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch saved articles',
        data: [],
      });
    }
  },

  // Get all news with pagination (v2.1.0 optimized)
  async getAllNews(page = 1, size = 50, sortBy = 'published_at', sortDirection = 'desc') {
    try {
      const response = await apiClient.get('/', {
        params: { page, size, sortBy, sortDirection }
      });
      
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        // Handle paginated response
        if (response.data.content && response.data.pagination) {
          const pagination = createPagination(response.data.pagination);
          const articles = response.data.content.map(article => createNewsArticle(article));
          return createPaginatedResponse(articles, pagination, apiResponse.message);
        }
        // Handle direct data response
        else if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(article => createNewsArticle(article));
        } else {
          apiResponse.data = createNewsArticle(apiResponse.data);
        }
      }
      
      return {
        ...apiResponse,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch news'
      });
    }
  },

  // Get news by ticker (v2.1.0 optimized)
  async getNewsByTicker(ticker, page = 1, size = 50) {
    try {
      const response = await apiClient.get(`/ticker/${ticker}`, {
        params: { page, size }
      });
      
      return {
        success: response.data.success,
        data: response.data.content,
        pagination: response.data.pagination,
        message: response.data.message,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch news by ticker'
      };
    }
  },

  // Get news by company name
  async getNewsByCompany(companyName, page = 1, size = 50) {
    try {
      const response = await apiClient.get(`/company/${encodeURIComponent(companyName)}`, {
        params: { page, size }
      });
      return {
        success: response.data.success,
        data: response.data.content,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch news by company'
      };
    }
  },

  // Search news articles
  async searchNews(query) {
    try {
      const response = await apiClient.get('/search', {
        params: { q: query }
      });
      
      return {
        success: response.data.success,
        data: response.data.data || [], // Backend returns articles directly in 'data' field
        message: response.data.message,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to search news'
      };
    }
  },

  // Get recent news by ticker using paginated endpoint (v2.2.0 - deprecated endpoint removed)
  async getRecentNewsByTicker(ticker, days = 7, page = 1, size = 50) {
    try {
      // Calculate date range for recent news
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - days);
      
      // Use date range endpoint with ticker filtering
      const response = await apiClient.get('/date-range', {
        params: { 
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          limit: size
        }
      });
      
      // Filter by ticker in memory since backend doesn't support ticker filtering in date-range
      let filteredData = response.data.data || [];
      if (ticker) {
        filteredData = filteredData.filter(article => 
          article.ticker === ticker || article.companyName?.toLowerCase().includes(ticker.toLowerCase())
        );
      }
      
      return {
        success: response.data.success,
        data: filteredData,
        message: response.data.message,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch recent news'
      };
    }
  },

  // Get news by risk category using fetch with criteria (v2.2.0 - deprecated endpoint removed)
  async getNewsByRiskCategory(riskCategory, page = 1, size = 50) {
    try {
      // Use fetch with criteria endpoint
      const criteria = {
        industryName: '',
        companyName: '',
        daysBack: 30,
        includeIndustryRisks: true,
        includeCompanyNews: true,
        riskCategories: [riskCategory],
        maxResults: size
      };
      
      const response = await apiClient.post('/fetch/paginated', criteria, {
        params: { page, size, saveToDatabase: false }
      });
      
      return {
        success: response.data.success,
        data: response.data.content || [],
        pagination: response.data.pagination,
        message: response.data.message,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch news by risk category'
      };
    }
  },

  // Get news statistics
  async getNewsStatistics() {
    try {
      const response = await apiClient.get('/statistics');
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        apiResponse.data = createNewsStatistics(apiResponse.data);
      }
      return apiResponse;
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch news statistics'
      });
    }
  },

  // Get news by date range
  async getNewsByDateRange(startDate, endDate, limit = 50) {
    try {
      const response = await apiClient.get('/date-range', {
        params: { startDate, endDate, limit }
      });
      
      const apiResponse = createApiResponse(response.data);
      if (apiResponse.success && apiResponse.data) {
        if (Array.isArray(apiResponse.data)) {
          apiResponse.data = apiResponse.data.map(article => createNewsArticle(article));
        } else {
          apiResponse.data = createNewsArticle(apiResponse.data);
        }
      }
      
      return {
        ...apiResponse,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return createApiResponse({
        success: false,
        message: error.response?.data?.message || error.message || 'Failed to fetch news by date range'
      });
    }
  },

  // Get relevant news
  async getRelevantNews(minScore = 0.5, limit = 50) {
    try {
      const response = await apiClient.get('/relevant', {
        params: { minScore, limit }
      });
      
      return {
        success: response.data.success,
        data: response.data.data || [], // Backend returns articles directly in 'data' field
        message: response.data.message,
        performance: {
          firebaseReads: response.headers['x-firebase-reads'],
          responseTime: response.headers['x-response-time']
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch relevant news'
      };
    }
  },

  // Fetch news with custom criteria (POST request)
  async fetchNewsWithCriteria(criteria, page = 1, size = 50, saveToDatabase = true) {
    try {
      const response = await apiClient.post('/fetch/paginated', criteria, {
        params: { page, size, saveToDatabase }
      });
      return {
        success: response.data.success,
        data: response.data.content,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch news with criteria'
      };
    }
  },

  // Get news by company name (alternative endpoint)
  async getNewsByCompanyName(companyName, page = 1, size = 50) {
    try {
      const response = await apiClient.get(`/company/${encodeURIComponent(companyName)}`, {
        params: { page, size }
      });
      return {
        success: response.data.success,
        data: response.data.content,
        pagination: response.data.pagination,
        message: response.data.message
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to fetch news by company name'
      };
    }
  },

  // Enhanced error handling for v2.2.0 optimized endpoints
  handleApiError(error, endpoint) {
    // Handle empty results
    if (error.response?.status === 404) {
      return {
        success: false,
        error: 'No news articles found for the specified criteria',
        errorCode: 'NO_RESULTS'
      };
    }
    
    // Handle rate limiting
    if (error.response?.status === 429) {
      return {
        success: false,
        error: 'Rate limit exceeded. Please wait a moment before trying again.',
        errorCode: 'RATE_LIMIT'
      };
    }
    
    // Handle server errors
    if (error.response?.status >= 500) {
      return {
        success: false,
        error: 'Server error occurred. Please try again later.',
        errorCode: 'SERVER_ERROR'
      };
    }
    
    // Generic error handling
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'An unexpected error occurred',
      errorCode: 'UNKNOWN_ERROR'
    };
  },

  // Performance metrics utility retained for call-site compatibility.
  logPerformanceMetrics() {},

  // Test method to verify API connectivity
  async testApiConnection() {
    try {
      const response = await apiClient.get('/statistics');
      return {
        success: true,
        message: 'API connection successful',
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'API connection failed'
      };
    }
  }
};
